/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/
import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Play, Pause, Volume2, VolumeX,
  Maximize, Minimize, RotateCcw, RotateCw, AlertCircle
} from 'lucide-react';

const formatTime = (seconds) => {
  if (!Number.isFinite(seconds) || seconds < 0) { return '0:00'; }
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const VideoPlayer = ({ src }) => {
  const containerRef = useRef(null);
  const videoRef = useRef(null);

  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  // Toggle play/pause
  const togglePlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused || v.ended) {
      v.play().catch(() => {});
    } else {
      v.pause();
    }
  }, []);

  // Seek handler
  const handleSeek = (e) => {
    const v = videoRef.current;
    if (!v) return;
    const time = Number(e.target.value);
    v.currentTime = time;
    setCurrentTime(time);
  };

  // Skip backward/forward by 5 seconds
  const handleSkip = (seconds) => {
    const v = videoRef.current;
    if (!v) return;
    const target = Math.max(0, Math.min(duration, v.currentTime + seconds));
    v.currentTime = target;
    setCurrentTime(target);
  };

  // Volume slider
  const handleVolume = (e) => {
    const val = Number(e.target.value);
    setVolume(val);
    if (videoRef.current) {
      videoRef.current.volume = val;
      videoRef.current.muted = val === 0;
    }
    setMuted(val === 0);
  };

  // Toggle mute
  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    if (muted || volume === 0) {
      const restoredVol = volume === 0 ? 0.5 : volume;
      v.muted = false;
      v.volume = restoredVol;
      setVolume(restoredVol);
      setMuted(false);
    } else {
      v.muted = true;
      setMuted(true);
    }
  };

  // Fullscreen toggle
  const toggleFullscreen = () => {
    const container = containerRef.current;
    if (!container) return;

    if (!document.fullscreenElement && !document.webkitFullscreenElement) {
      if (container.requestFullscreen) {
        container.requestFullscreen().catch(() => {});
      } else if (container.webkitRequestFullscreen) {
        container.webkitRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen().catch(() => {});
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      }
    }
  };

  // Listen for fullscreen change
  useEffect(() => {
    const onFullscreenChange = () => {
      setIsFullscreen(!!(document.fullscreenElement || document.webkitFullscreenElement));
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    document.addEventListener('webkitfullscreenchange', onFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', onFullscreenChange);
    };
  }, []);

  // Keyboard shortcut listener when container is hovered/focused
  const handleKeyDown = (e) => {
    if (['INPUT', 'TEXTAREA', 'BUTTON'].includes(e.target.tagName)) return;
    if (e.key === ' ' || e.key === 'k') {
      e.preventDefault();
      togglePlay();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      handleSkip(-5);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      handleSkip(5);
    } else if (e.key === 'm' || e.key === 'M') {
      e.preventDefault();
      toggleMute();
    } else if (e.key === 'f' || e.key === 'F') {
      e.preventDefault();
      toggleFullscreen();
    }
  };

  return (
    <div
      ref={containerRef}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      className="w-full h-full min-h-0 flex flex-col justify-between bg-black overflow-hidden relative group/player select-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
    >
      {/* ------------------------------------------------------------------ */}
      {/* Video Display Stage                                               */}
      {/* ------------------------------------------------------------------ */}
      <div className="flex-1 min-h-0 w-full relative flex items-center justify-center bg-black overflow-hidden">
        <video
          ref={videoRef}
          src={src}
          className="w-full h-full max-h-full object-contain cursor-pointer"
          onClick={togglePlay}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => setPlaying(false)}
          onTimeUpdate={(e) => setCurrentTime(e.target.currentTime)}
          onLoadedMetadata={(e) => {
            setDuration(e.target.duration);
            setLoading(false);
            setHasError(false);
          }}
          onWaiting={() => setLoading(true)}
          onCanPlay={() => setLoading(false)}
          onError={() => {
            setLoading(false);
            setHasError(true);
          }}
          playsInline
        />

        {/* Loading / Buffering Spinner */}
        {loading && !hasError && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/40 pointer-events-none z-10">
            <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin shadow-lg" />
          </div>
        )}

        {/* Error State */}
        {hasError && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-base-900/90 text-center p-4 z-10 space-y-2">
            <AlertCircle size={36} className="text-error" />
            <p className="text-sm font-bold text-white">Unable to load video preview</p>
            <p className="text-xs text-white/60 max-w-xs">
              The video format or network connection could not be processed by the player.
            </p>
          </div>
        )}

        {/* Center Play Button Overlay (when paused & not loading/error) */}
        {!playing && !loading && !hasError && (
          <button
            type="button"
            onClick={togglePlay}
            className="absolute inset-0 m-auto w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-black/60 hover:bg-primary/90 text-white flex items-center justify-center shadow-2xl backdrop-blur-sm transition-all duration-200 hover:scale-110 active:scale-95 z-10"
            title="Play Video"
            aria-label="Play Video"
          >
            <Play size={26} className="fill-white ml-1" />
          </button>
        )}
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Docked Bottom Controls Bar (Always Visible)                        */}
      {/* ------------------------------------------------------------------ */}
      <div className="flex-shrink-0 bg-base-200/95 dark:bg-base-900/95 backdrop-blur-md px-3 sm:px-4 py-2.5 flex items-center gap-2 sm:gap-3 border-t border-base-content/10 select-none z-20">
        {/* Play/Pause Button */}
        <button
          type="button"
          onClick={togglePlay}
          className="flex-shrink-0 p-2 rounded-full bg-primary text-primary-content hover:brightness-110 transition shadow-sm active:scale-95"
          title={playing ? 'Pause (Space)' : 'Play (Space)'}
          aria-label={playing ? 'Pause (Space)' : 'Play (Space)'}
        >
          {playing ? <Pause size={15} /> : <Play size={15} className="ml-0.5" />}
        </button>

        {/* Skip 5s Back */}
        <button
          type="button"
          onClick={() => handleSkip(-5)}
          className="flex-shrink-0 p-1.5 text-base-content/60 hover:text-primary transition rounded-lg hover:bg-base-content/5 hidden sm:flex items-center justify-center"
          title="Rewind 5s (←)"
          aria-label="Rewind 5s (←)"
        >
          <RotateCcw size={14} />
        </button>

        {/* Skip 5s Forward */}
        <button
          type="button"
          onClick={() => handleSkip(5)}
          className="flex-shrink-0 p-1.5 text-base-content/60 hover:text-primary transition rounded-lg hover:bg-base-content/5 hidden sm:flex items-center justify-center"
          title="Forward 5s (→)"
          aria-label="Forward 5s (→)"
        >
          <RotateCw size={14} />
        </button>

        {/* Current Time */}
        <span className="text-xs font-mono font-bold text-base-content/80 flex-shrink-0">
          {formatTime(currentTime)}
        </span>

        {/* Scrub Bar / Range Slider */}
        <input
          type="range"
          min={0}
          max={duration || 0}
          step={0.1}
          value={currentTime}
          onChange={handleSeek}
          className="range range-primary range-xs flex-1 cursor-pointer"
          title={`Seek: ${formatTime(currentTime)} / ${formatTime(duration)}`}
          aria-label={`Seek: ${formatTime(currentTime)} / ${formatTime(duration)}`}
        />

        {/* Duration */}
        <span className="text-xs font-mono font-bold text-base-content/50 flex-shrink-0">
          {formatTime(duration)}
        </span>

        {/* Mute Toggle */}
        <button
          type="button"
          onClick={toggleMute}
          className="flex-shrink-0 p-1.5 text-base-content/60 hover:text-primary transition rounded-lg hover:bg-base-content/5"
          title={muted || volume === 0 ? 'Unmute (M)' : 'Mute (M)'}
          aria-label={muted || volume === 0 ? 'Unmute (M)' : 'Mute (M)'}
        >
          {muted || volume === 0 ? <VolumeX size={16} /> : <Volume2 size={16} />}
        </button>

        {/* Volume Slider */}
        <input
          type="range"
          min={0}
          max={1}
          step={0.05}
          value={muted ? 0 : volume}
          onChange={handleVolume}
          className="range range-primary range-xs w-16 sm:w-20 flex-shrink-0 hidden sm:block cursor-pointer"
          title={`Volume: ${Math.round((muted ? 0 : volume) * 100)}%`}
          aria-label={`Volume: ${Math.round((muted ? 0 : volume) * 100)}%`}
        />

        {/* Fullscreen Toggle */}
        <button
          type="button"
          onClick={toggleFullscreen}
          className="flex-shrink-0 p-1.5 text-base-content/60 hover:text-primary transition rounded-lg hover:bg-base-content/5"
          title={isFullscreen ? 'Exit Fullscreen (F)' : 'Fullscreen (F)'}
          aria-label={isFullscreen ? 'Exit Fullscreen (F)' : 'Fullscreen (F)'}
        >
          {isFullscreen ? <Minimize size={15} /> : <Maximize size={15} />}
        </button>
      </div>
    </div>
  );
};

export default VideoPlayer;

VideoPlayer.propTypes = {
  src: PropTypes.string.isRequired,
};
